/**
 * analytics/AnalyticsChatPage.jsx
 *
 * Full-page wrapper for Phase 2 conversational analytics.
 * Route: /mrs/:mrsId/analytics-chat
 *
 * Flow:
 *   1. Load all datasets for the MRS on mount.
 *   2. Auto-select ALL datasets and auto-start a session — no blocking gate.
 *   3. Show ChatInterface immediately.
 *   4. A collapsible dataset tray at the top lets the user change the
 *      selection or upload a new dataset without leaving the chat.
 *
 * If no datasets exist yet, show the upload panel inline before the chat.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../App';
import DatasetSelector from './DatasetSelector';
import ChatInterface from './ChatInterface';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const ANALYTICS_API = `${BACKEND_URL}/api/analytics`;

const AnalyticsChatPage = () => {
  const { mrsId }    = useParams();
  const { user }     = useAuth();
  const navigate     = useNavigate();
  const userId       = user?.id || '';

  // Session state
  const [session, setSession]           = useState(null);
  const [sessionLoading, setSessionLoading] = useState(false);
  const [sessionError, setSessionError] = useState(null);

  // Dataset list state
  const [datasets, setDatasets]         = useState([]);
  const [datasetsLoading, setDatasetsLoading] = useState(true);

  // UI state
  const [showDatasetTray, setShowDatasetTray] = useState(false);

  /* ── Load datasets ──────────────────────────────────────────────────── */
  const loadDatasets = useCallback(() => {
    if (!mrsId || !userId) return;
    setDatasetsLoading(true);
    axios
      .get(`${ANALYTICS_API}/mrs/${mrsId}/datasets?user_id=${userId}`)
      .then((res) => {
        const list = res.data.datasets || [];
        setDatasets(list);
      })
      .catch(() => setDatasets([]))
      .finally(() => setDatasetsLoading(false));
  }, [mrsId, userId]);

  useEffect(() => { loadDatasets(); }, [loadDatasets]);

  /* ── Auto-start session once datasets are loaded ────────────────────── */
  useEffect(() => {
    // Only auto-start if we have datasets and no session yet
    if (datasetsLoading || datasets.length === 0 || session) return;

    const allIds = datasets.map((d) => d.dataset_id);
    autoStartSession(allIds);
  }, [datasetsLoading, datasets]); // eslint-disable-line

  const autoStartSession = async (datasetIds) => {
    setSessionLoading(true);
    setSessionError(null);
    try {
      const res = await axios.post(
        `${ANALYTICS_API}/sessions/start?user_id=${userId}`,
        { mrs_id: mrsId, dataset_ids: datasetIds }
      );
      setSession(res.data);
    } catch (err) {
      const msg =
        err?.response?.data?.detail?.message ||
        'Failed to start analysis session. Please try again.';
      setSessionError(msg);
    } finally {
      setSessionLoading(false);
    }
  };

  /* ── Dataset tray: user changed selection → restart session ─────────── */
  const handleSessionStart = (newSession) => {
    setSession(newSession);
    setShowDatasetTray(false);
  };

  const handleSessionEnd = () => {
    setSession(null);
    setShowDatasetTray(true); // open tray so user can pick different datasets
  };

  /* ── Refresh datasets + restart after upload ────────────────────────── */
  const handleDatasetsChanged = () => {
    loadDatasets();
    setSession(null); // will re-trigger auto-start after reload
  };

  /* ── Loading screen ─────────────────────────────────────────────────── */
  if (datasetsLoading) {
    return (
      <div className="flex flex-col h-[calc(100vh-8rem)]">
        <PageHeader mrsId={mrsId} />
        <div className="flex-1 flex items-center justify-center text-gray-400 text-sm">
          Loading datasets…
        </div>
      </div>
    );
  }

  /* ── No datasets yet: show upload panel ─────────────────────────────── */
  if (datasets.length === 0) {
    return (
      <div className="flex flex-col h-[calc(100vh-8rem)]">
        <PageHeader mrsId={mrsId} />
        <div className="flex-1 flex items-center justify-center">
          <div className="max-w-xl w-full bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-sky-100 flex items-center justify-center shrink-0">
                <svg className="w-5 h-5 text-sky-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                </svg>
              </div>
              <div>
                <h2 className="text-base font-semibold text-gray-900">Upload your first dataset</h2>
                <p className="text-sm text-gray-500">
                  Upload an Excel or CSV file to start analysing with AI.
                </p>
              </div>
            </div>
            <DatasetSelector
              mrsId={mrsId}
              userId={userId}
              onSessionStart={handleSessionStart}
              onDatasetsChanged={handleDatasetsChanged}
            />
          </div>
        </div>
      </div>
    );
  }

  /* ── Session starting ───────────────────────────────────────────────── */
  if (sessionLoading) {
    return (
      <div className="flex flex-col h-[calc(100vh-8rem)]">
        <PageHeader mrsId={mrsId} />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <div className="w-8 h-8 border-2 border-sky-500 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
            <p className="text-sm text-gray-500">
              Starting analysis session with {datasets.length} dataset{datasets.length !== 1 ? 's' : ''}…
            </p>
          </div>
        </div>
      </div>
    );
  }

  /* ── Session error ──────────────────────────────────────────────────── */
  if (sessionError && !session) {
    return (
      <div className="flex flex-col h-[calc(100vh-8rem)]">
        <PageHeader mrsId={mrsId} />
        <div className="flex-1 flex items-center justify-center">
          <div className="max-w-md w-full text-center">
            <p className="text-red-600 text-sm mb-4">{sessionError}</p>
            <button
              onClick={() => autoStartSession(datasets.map((d) => d.dataset_id))}
              className="px-4 py-2 bg-sky-600 text-white text-sm rounded-lg hover:bg-sky-700"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  /* ── Main: session active ───────────────────────────────────────────── */
  return (
    <div className="flex flex-col h-[calc(100vh-8rem)]">

      {/* ── Header with dataset info ──────────────────────────────────── */}
      <div className="flex items-center justify-between mb-3 shrink-0">
        <div>
          <nav className="text-xs text-gray-400 mb-0.5 flex items-center gap-1">
            <Link to="/mrs" className="hover:text-sky-600">MRS</Link>
            <span>›</span>
            <span className="text-gray-600 font-medium">AI Analytics</span>
          </nav>
          <div className="flex items-center gap-2">
            <h1 className="text-lg font-bold text-gray-900">Analyse Your Data with AI</h1>
            {session && (
              <span className="text-xs font-normal bg-sky-100 text-sky-700 px-2 py-0.5 rounded-full">
                {session.dataset_ids?.length || 0} dataset{session.dataset_ids?.length !== 1 ? 's' : ''} active
              </span>
            )}
          </div>
        </div>

        {/* Dataset tray toggle */}
        <button
          onClick={() => setShowDatasetTray((v) => !v)}
          className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-800
                     border border-gray-200 rounded-lg px-3 py-1.5 hover:bg-gray-50 transition-colors"
          title="Change datasets or upload new data"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
              d="M4 6h16M4 12h16M4 18h7" />
          </svg>
          {showDatasetTray ? 'Hide datasets' : 'Datasets'}
        </button>
      </div>

      {/* ── Collapsible dataset tray ──────────────────────────────────── */}
      {showDatasetTray && (
        <div className="mb-3 shrink-0 bg-white border border-gray-200 rounded-xl shadow-sm p-4">
          <p className="text-sm font-medium text-gray-700 mb-3">
            Change dataset selection or upload a new file. Click "Start Analysis" to restart the session.
          </p>
          <DatasetSelector
            mrsId={mrsId}
            userId={userId}
            onSessionStart={handleSessionStart}
            onDatasetsChanged={handleDatasetsChanged}
            compact
            initialSelected={session?.dataset_ids || []}
          />
        </div>
      )}

      {/* ── Chat area ─────────────────────────────────────────────────── */}
      {session && (
        <div className="flex-1 min-h-0">
          <ChatInterface
            session={session}
            onSessionEnd={handleSessionEnd}
          />
        </div>
      )}
    </div>
  );
};

/* ── Minimal page header (used in loading/error states) ─────────────────── */
const PageHeader = ({ mrsId }) => (
  <div className="mb-4 shrink-0">
    <nav className="text-xs text-gray-400 mb-0.5 flex items-center gap-1">
      <Link to="/mrs" className="hover:text-sky-600">MRS</Link>
      <span>›</span>
      <span className="text-gray-600 font-medium">AI Analytics</span>
    </nav>
    <h1 className="text-lg font-bold text-gray-900">Analyse Your Data with AI</h1>
  </div>
);

export default AnalyticsChatPage;

